import ArrowBack from '@mui/icons-material/ArrowBack';
import { AlertColor, Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import axios from 'axios';
import Notification from 'components/Notification';
import PageLoader from 'components/PageLoader';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import PreviewContract from 'screens/Authenticated/NewBooking/PreviewContract';
import { PreviewPopover } from 'screens/Authenticated/NewBooking/PreviewPopover';
import Config from 'utils/Config';
import axiosInstance from 'utils/axios';
import { identifiers } from 'utils/constants/identifiers';
import { routes } from 'utils/constants/routes';
import { URLS } from 'utils/constants/urls';
import { useCustomNavigate } from 'utils/hooks/useCustomNavigate';
import { fetchFromStorage } from 'utils/storage';
import { downloadExportFile, getFileName } from 'utils/validators/HelperFunctions';
import strings from '../../../common/Translation/Translate';
import { Privilege } from '../../../utils/redux/reducer/authentication-slice';
import { bookingDetailToolbarBackButtonSx } from './bookingDetailToolbarSx';

const CONTRACT = identifiers.CONTRACT;
const PROPOSAL = identifiers.PROPOSAL;
const PI = identifiers.PI;
const ENQUIRY = identifiers.ENQUIRY;

type BookingDetailActionsContextValue = ReturnType<typeof useBookingDetailActionsModel>;

const BookingDetailActionsContext = createContext<BookingDetailActionsContextValue | null>(null);

function useBookingDetailActionsModel(
  bookingLineItem: any,
  futureDateForCancel: boolean,
  futureDateForBeo: boolean,
  onSuccess: () => void,
) {
  const dispatch = useDispatch();
  const { id: lineItemIdParam } = useParams() as { id: string };
  const lineItemId = Number(lineItemIdParam);
  const IsPrivilege = useSelector(Privilege);
  const Navigate = useCustomNavigate();
  const token = fetchFromStorage(identifiers.access_token);
  const [previewData, setPreviewData] = useState('');
  const [loading, setLoading] = useState(false);
  const [closeModal, setCloseModal] = useState<string | false>(false);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: any;
  }>({
    display: false,
    severity: null,
    message: '',
  });

  const handlePreviewContract = useCallback(
    async (type: any) => {
      try {
        setLoading(true);
        const { status, data } = await axiosInstance.get(
          `${URLS.bookingPreviewTemplateById(lineItemId)}?type=${type}`,
        );
        if (status === 200) {
          setLoading(false);
          setCloseModal(type);
          setPreviewData(data);
        }
      } catch (e: any) {
        setLoading(false);
        if (e.response) {
          setMessage({
            display: true,
            severity: 'error',
            message: e.response?.data.message,
          });
        } else {
          setMessage({
            display: true,
            severity: 'error',
            message: e.message,
          });
        }
      }
    },
    [lineItemId],
  );

  const fetchPreviewPdf = useCallback(
    async (type: string, format: string = 'PDF') => {
      const documentFormat = format === 'DOCX' ? 'DOCX' : 'PDF';
      const response = await axios({
        url: `${Config.BaseURL + URLS.bookingPreviewTemplatePdfById(lineItemId)}?type=${type}&format=${documentFormat}`,
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      return response;
    },
    [lineItemId, token],
  );

  const downloadContract = useCallback(
    async (type: any, format: string = 'PDF') => {
      const documentFormat = format === 'DOCX' ? 'DOCX' : 'PDF';
      setLoading(true);
      try {
        const response = await fetchPreviewPdf(type, documentFormat);
        downloadExportFile(response, documentFormat.toLowerCase(), getFileName(type));
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.forbidden,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchPreviewPdf],
  );

  const prepareShareFile = useCallback(
    async (type: string) => {
      setLoading(true);
      try {
        const response = await fetchPreviewPdf(type);
        const name = getFileName(type) || 'document';
        const fileName = name.toLowerCase().endsWith('.pdf') ? name : `${name}.pdf`;
        return { blob: response.data as Blob, fileName };
      } catch (err: any) {
        if (err?.response?.status === 403) {
          setMessage({
            display: true,
            severity: 'error',
            message: strings.forbidden,
          });
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fetchPreviewPdf],
  );

  const closeNotification = useCallback((value: boolean) => {
    setMessage(prev => ({ ...prev, display: value }));
  }, []);

  const getBeoButtonText = useCallback((futureBeo: boolean, booking: any) => {
    if (!futureBeo && !booking?.beo?.id) return strings.no_beo_added_button;
    if (!futureBeo) return strings.BEO_details_text;
    return booking.beo?.id ? strings.update_BEO_text : strings.add_BEO_Text;
  }, []);

  const isBeoButtonDisabled = useCallback(
    (futureBeo: boolean, booking: any) => !futureBeo && !booking?.beo?.id,
    [],
  );

  return {
    bookingLineItem,
    futureDateForCancel,
    futureDateForBeo,
    onSuccess,
    dispatch,
    Navigate,
    IsPrivilege,
    lineItemId,
    loading,
    previewData,
    closeModal,
    setCloseModal,
    message,
    setMessage,
    handlePreviewContract,
    downloadContract,
    prepareShareFile,
    closeNotification,
    getBeoButtonText,
    isBeoButtonDisabled,
  };
}

type ProviderProps = {
  children: ReactNode;
  bookingLineItem: any;
  futureDateForCancel: boolean;
  futureDateForBeo: boolean;
  onSuccess: () => void;
};

export function BookingDetailActionsProvider({
  children,
  bookingLineItem,
  futureDateForCancel,
  futureDateForBeo,
  onSuccess,
}: ProviderProps) {
  const value = useBookingDetailActionsModel(
    bookingLineItem,
    futureDateForCancel,
    futureDateForBeo,
    onSuccess,
  );
  return <BookingDetailActionsContext.Provider value={value}>{children}</BookingDetailActionsContext.Provider>;
}

export function useBookingDetailActions() {
  const ctx = useContext(BookingDetailActionsContext);
  if (!ctx) throw new Error('useBookingDetailActions must be used within BookingDetailActionsProvider');
  return ctx;
}

export function BookingDetailTopToolbar() {
  const navigate = useNavigate();
  const { bookingLineItem } = useBookingDetailActions();

  if (!bookingLineItem?.id) {
    return null;
  }

  /** Primary actions live in {@link BookingDetailStickyHeader}; keep toolbar only for deleted bookings. */
  if (!bookingLineItem.deleted) {
    return null;
  }

  return (
    <Paper elevation={0} sx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 0, px: 2, py: 1.25, mb: 0 }}>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<ArrowBack />}
        onClick={() => navigate(routes.bookings)}
        sx={{ ...bookingDetailToolbarBackButtonSx }}>
        {strings.bookingDetails_backToDiary}
      </Button>
    </Paper>
  );
}

export function BookingDetailBottomPreview() {
  const ctx = useBookingDetailActions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    bookingLineItem,
    loading,
    previewData,
    closeModal,
    setCloseModal,
    message,
    closeNotification,
    handlePreviewContract,
    downloadContract,
    prepareShareFile,
  } = ctx;

  const shareEmail = bookingLineItem?.booking?.email || '';

  if (!bookingLineItem?.id || bookingLineItem.deleted) {
    return null;
  }

  const hasTemplates =
    bookingLineItem.booking?.isContractTemplatePresent ||
    bookingLineItem.booking?.isProposalTemplatePresent ||
    bookingLineItem.booking?.isPiTemplatePresent ||
    bookingLineItem.booking?.isEnquiryTemplatePresent;

  if (!hasTemplates) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
        mb: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
      }}>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {loading && <PageLoader />}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600, textAlign: 'center' }}>
        {isMobile ? strings.bookingDetails_downloadSectionTitle : strings.bookingDetails_previewSectionTitle}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <PreviewPopover
          inline
          isContractTemplatePresent={bookingLineItem.booking.isContractTemplatePresent}
          isProposalTemplatePresent={bookingLineItem.booking.isProposalTemplatePresent}
          isPiTemplatePresent={bookingLineItem.booking.isPiTemplatePresent}
          isEnquiryTemplatePresent={
            bookingLineItem.status === 'ENQUIRY' && bookingLineItem.booking.isEnquiryTemplatePresent
          }
          downloadContract={downloadContract}
          handlePreviewContract={handlePreviewContract}
        />
      </Box>

      {closeModal === CONTRACT && (
        <PreviewContract
          headerText={strings.bookings_button_preview_contract}
          downloadButtonText={strings.bookings_button_download_contract}
          downloadContract={downloadContract}
          prepareShareFile={prepareShareFile}
          shareEmail={shareEmail}
          pdfUrl={previewData}
          handleClose={() => setCloseModal(false)}
          openPdfDrawer={closeModal}
          loading={loading}
        />
      )}
      {closeModal === PROPOSAL && (
        <PreviewContract
          headerText={strings.bookings_button_preview_proposal}
          downloadButtonText={strings.bookings_button_download_proposal}
          downloadContract={downloadContract}
          prepareShareFile={prepareShareFile}
          shareEmail={shareEmail}
          pdfUrl={previewData}
          handleClose={() => setCloseModal(false)}
          openPdfDrawer={closeModal}
          loading={loading}
        />
      )}
      {closeModal === PI && (
        <PreviewContract
          headerText={strings.bookings_button_preview_pi}
          downloadButtonText={strings.bookings_button_download_pi}
          downloadContract={downloadContract}
          prepareShareFile={prepareShareFile}
          shareEmail={shareEmail}
          pdfUrl={previewData}
          handleClose={() => setCloseModal(false)}
          openPdfDrawer={closeModal}
          loading={loading}
        />
      )}
      {closeModal === ENQUIRY && (
        <PreviewContract
          headerText={strings.bookings_button_preview_enquiry}
          downloadButtonText={strings.bookings_button_download_enquiry}
          downloadContract={downloadContract}
          prepareShareFile={prepareShareFile}
          shareEmail={shareEmail}
          pdfUrl={previewData}
          handleClose={() => setCloseModal(false)}
          openPdfDrawer={closeModal}
          loading={loading}
        />
      )}
    </Box>
  );
}
